import React, { useMemo, useState } from "react";
import DocumentPreviewModal from "../../common/DocumentPreview/DocumentPreviewModal";
import { buildDocumentDownloadUrl } from "../../common/DocumentPreview/documentUrlUtils";
import "../qms/QmsPages.scss";
import utils from "../../../utils/utils";

function pick(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function getDocName(doc, index = 0) {
  return (
    String(
      pick(doc, "DocumentName", "documentName", "FileName", "fileName") || "",
    ).trim() || `Document ${index + 1}`
  );
}

function getDocUrl(doc) {
  const raw = String(
    pick(
      doc,
      "DocumentUrl",
      "documentUrl",
      "DocumentURL",
      "FileUrl",
      "fileUrl",
      "Url",
    ) || "",
  ).trim();
  return buildDocumentDownloadUrl(raw) || raw;
}

function getExtension(nameOrUrl) {
  const text = String(nameOrUrl || "").split("?")[0].trim();
  const match = text.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

function formatDocDate(value) {
  if (value == null || value === "") return "";
  const text = String(value).trim();
  if (!text || text === "-" || text === "—") return "";
  // SP already formats as "19 Jul 2026 5:30 PM"
  if (/^\d{1,2}\s+[A-Za-z]{3}\s+\d{4}/.test(text)) return text;
  if (typeof utils.formatDateTimeDisplay === "function") {
    const formatted = utils.formatDateTimeDisplay(text);
    if (formatted && formatted !== "—") return formatted;
  }
  return text;
}

function docSortTime(value) {
  const text = String(value || "").trim();
  if (!text) return 0;
  const parsed = Date.parse(text);
  if (!Number.isNaN(parsed)) return parsed;
  // "02 Aug 2026 7:07 PM" → sortable timestamp
  const m = text.match(
    /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i,
  );
  if (!m) return 0;
  const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const ampm = (m[6] || "").toUpperCase();
  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  const month = months[m[2].toLowerCase()];
  if (month == null) return 0;
  return new Date(Number(m[3]), month, Number(m[1]), hour, minute).getTime();
}

function fileIconClass(ext) {
  if (["pdf"].includes(ext)) return "fas fa-file-pdf";
  if (["doc", "docx"].includes(ext)) return "fas fa-file-word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "fas fa-file-excel";
  if (["ppt", "pptx"].includes(ext)) return "fas fa-file-powerpoint";
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "fas fa-file-image";
  }
  if (["txt", "rtf"].includes(ext)) return "fas fa-file-alt";
  if (["zip", "rar", "7z"].includes(ext)) return "fas fa-file-archive";
  return "fas fa-file";
}

function fileTypeLabel(ext) {
  if (!ext) return "File";
  return ext.toUpperCase();
}

function DocumentsPanel({
  documents = [],
  fileInputRef,
  onAddClick,
  onFileChange,
  canEdit = false,
}) {
  const [preview, setPreview] = useState(null);

  const rows = useMemo(() => {
    const mapped = (Array.isArray(documents) ? documents : []).map(
      (doc, index) => {
        const name = getDocName(doc, index);
        const url = getDocUrl(doc);
        const ext =
          String(
            pick(doc, "DocumentExtension", "documentExtension") || "",
          ).trim() ||
          getExtension(name) ||
          getExtension(url);
        const uploadedOnRaw = pick(
          doc,
          "SystemDateTime",
          "systemDateTime",
          "CreatedDate",
          "CreatedOn",
          "UploadedOn",
          "UploadedDate",
          "UploadedDateTime",
        );
        const uploadedBy = String(
          pick(
            doc,
            "CreatedBy",
            "createdBy",
            "UploadedBy",
            "uploadedBy",
            "CreatedByName",
            "UploadedByName",
          ) || "",
        )
          .replace(/\s+/g, " ")
          .trim();
        const sortTime = docSortTime(uploadedOnRaw);
        return {
          key: `${name}-${index}`,
          doc,
          name,
          url,
          ext,
          documentId: String(
            pick(doc, "DocumentId", "documentId", "DocumentIdRef") || "",
          ).trim(),
          typeLabel: fileTypeLabel(ext),
          uploadedOn: uploadedOnRaw ? formatDocDate(uploadedOnRaw) : "",
          uploadedBy,
          sortTime,
        };
      },
    );
    return mapped.sort((a, b) => b.sortTime - a.sortTime);
  }, [documents]);

  const openPreview = (row) => {
    if (!row?.url) return;
    // Preview needs a real public blob https URL (not inline data:).
    if (/data:/i.test(String(row.url))) {
      window.alert(
        "This document was not uploaded to storage correctly, so it cannot be previewed. Please re-upload the file.",
      );
      return;
    }
    const meta = [row.typeLabel, row.uploadedOn, row.uploadedBy]
      .filter(Boolean)
      .join(" · ");
    setPreview({
      documentId: row.documentId,
      documentUrl: row.url,
      documentExtension: row.ext,
      documentTitle: row.name,
      documentCode: meta,
    });
  };

  return (
    <div className="pm-documents-tab">
      <div className="pm-documents-tab__head">
        <div>
          <h3 className="pm-details-section__title mb-0">Documents</h3>
          <p className="pm-documents-tab__count">
            {rows.length
              ? `${rows.length} document${rows.length === 1 ? "" : "s"}`
              : "No documents yet"}
          </p>
        </div>
        {canEdit ? (
          <>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={onAddClick}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add document
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              onChange={onFileChange}
            />
          </>
        ) : null}
      </div>

      {rows.length ? (
        <div className="pm-doc-cards pm-thin-scroll">
          {rows.map((row) => (
            <article key={row.key} className="pm-doc-card">
              <div className="pm-doc-card__icon" aria-hidden="true">
                <i className={fileIconClass(row.ext)} />
              </div>
              <div className="pm-doc-card__body">
                <h4 className="pm-doc-card__name" title={row.name}>
                  {row.name}
                </h4>
                <div className="pm-doc-card__meta">
                  <span className="pm-doc-card__type">{row.typeLabel}</span>
                  {row.uploadedOn ? <span>{row.uploadedOn}</span> : null}
                  {row.uploadedBy ? <span>{row.uploadedBy}</span> : null}
                </div>
              </div>
              <div className="pm-doc-card__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={!row.url || /data:/i.test(String(row.url))}
                  title={
                    /data:/i.test(String(row.url))
                      ? "This file was not stored correctly. Please re-upload it."
                      : "Preview"
                  }
                  onClick={() => openPreview(row)}
                >
                  Preview
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="pm-empty-state">
          No documents uploaded yet.
          {canEdit ? " Use Add document to upload one." : ""}
        </div>
      )}

      <DocumentPreviewModal
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        sourceModule="VOLUNTEER"
        sourceTable="VolunteerDocument"
        documentId={preview?.documentId || ""}
        documentUrl={preview?.documentUrl || ""}
        documentExtension={preview?.documentExtension || ""}
        documentTitle={preview?.documentTitle || "Document"}
        documentCode={preview?.documentCode || ""}
      />
    </div>
  );
}

export default DocumentsPanel;
